import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/core';
import { MenuService } from '../../../../layout/services/menu.service';
import { MonitoringAlarmSection, MonitoringHost } from '../../../project/models/project-configuration';
import { MenuBehaviorSubjectService } from '../../../../shared/services/menu-behavior-subject.service';
import { Subscription, Observable, BehaviorSubject, interval, of, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TableStateService } from '../../../../shared/services/table-state.service';
import { FaultMonitoringDataService } from "../../../../features/monitoring/services/fault-monitoring-data.service";
import { MonitoringAlarm } from '../../models/monitoring-alarm';
import { MessageService, SelectItem } from 'primeng/api';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { ReferenceArea } from '../../../reference-areas/models/reference-area';
import { ReferenceAreaDataService } from '../../../reference-areas/services/reference-area-data.service';
import { MonitoringZabbixHost } from "../../../../features/monitoring/models/monitoring-zabbix-host";

@Component({
  selector: 'app-monitoring-alarms',
  template: require('./monitoring-alarms.component.html'),
  styles: [require('./monitoring-alarms.component.sass')]
})
export class MonitoringAlarmsComponent implements OnInit, OnDestroy {

  referenceAreas$: Observable<ReferenceArea[]>;
  displayMap: boolean | undefined;

  monitoringAlarmSection: MonitoringAlarmSection | undefined;
  menuChangeSubscription: Subscription;
  languageChangeSubscription: Subscription;
  pollingMonitoringAlarmsSubscription: Subscription;

  hostObjects: MonitoringZabbixHost[] | undefined;

  alarms$: Observable<MonitoringAlarm[]>;
  selectedAlarmList: MonitoringAlarm[] = [];
  polling$: Observable<number | undefined>;

  alarmDateFormat = environment.alarms.dateFormat;

  url: string;
  pageTitle: string | undefined;
  projectId: number;

  tableConfiguration: any;
  zoom: number = environment.config.map.trainInformation.defaultZoom
  alarmSearchTerm: string;

  alarmTableFilters: any;
  autoRefresh: boolean;

  columns = [{
    header: 'I18N_MONITORING.HOST',
    field: 'hostname',
    type: 'text'
  }, {
    header: 'I18N_MONITORING.SEVERITY',
    field: 'severity_label',
    type: 'severity',
    width: '200px'
  }, {
    header: 'I18N_MONITORING.DESCRIPTION',
    field: 'description',
    type: 'text'
  }, {
    header: 'I18N_MONITORING.DATE',
    field: 'lastChange',
    sortOrder: '-1'
  }, {
    header: 'I18N_MONITORING.ACKNOWLEDGED',
    field: 'acknowledged',
    type: 'acknowledged'
  }];

  acknowledgedOptions: SelectItem[];
  selectedAcknowledgedOption: boolean | null = false;
  severityOptions: SelectItem[];
  selectedSeverityOption: number | null = null;

  @ViewChild('dt') dt!: any;

  @ViewChild('alarmContent') set alarmContent(element) {
    if (element) {
      this.dt.filter(this.selectedAcknowledgedOption, 'acknowledged', 'equals')
    }
  }
  private readonly refreshAlarms$ = new BehaviorSubject<string>('');
  private readonly refreshReferenceAreas$ = new BehaviorSubject(undefined);

  constructor(
    public tableState: TableStateService,
    private $state: StateService,
    private menuService: MenuService,
    private translate: TranslateService,
    private menuBehaviorSubjectService: MenuBehaviorSubjectService,
    private cdref: ChangeDetectorRef,
    private faultMonitoringDataService: FaultMonitoringDataService,
    private referenceAreaDataService: ReferenceAreaDataService,
    private messageService: MessageService,
  ) {
    this.url = this.$state.params.alarmType;
    this.projectId = this.$state.params && this.$state.params.projectId;

    const customAlarmPageFilters = localStorage.getItem('custom-page-alarm-filters-' + this.projectId);
    if (customAlarmPageFilters) {
      this.autoRefresh = JSON.parse(customAlarmPageFilters).autoRefresh;
    } else {
      this.autoRefresh = true;
      localStorage.setItem(
        `custom-page-alarm-filters-${this.projectId}`,
        JSON.stringify({
          autoRefresh: this.autoRefresh
        })
      );
    }


    this.referenceAreas$ = this.refreshReferenceAreas$.pipe(
      switchMap(() => this.referenceAreaDataService.getReferenceAreas(this.projectId)));

    this.polling$ = this.refreshAlarms$.pipe(
      switchMap(() => {
        return interval(environment.config.monitoring.refreshPeriod * 1000)
      })
    )

    this.pollingMonitoringAlarmsSubscription = merge(
      this.refreshAlarms$,
      this.polling$
    ).pipe(
      switchMap((updateReason) => {
        const customAlarmPageFilters = localStorage.getItem('custom-page-alarm-filters-' + this.projectId);
        const autoRefresh = customAlarmPageFilters && JSON.parse(customAlarmPageFilters).autoRefresh;
        if (updateReason === 'refresh' || autoRefresh) {
          return this.faultMonitoringDataService.getMonitoringHosts(this.projectId, this.url).pipe(
            map(hosts => hosts.map(host => {
              return {
                ...host,
                issues: host.issues.map(issue => {
                  return {
                    ...issue,
                    severity_label: this.translate.instant(issue.severity_label)
                  }
                })
              }
            }))
          )
        }
        return of(null)
      }))
      .subscribe((hosts) => {
        if (hosts !== null) {
          const alarms = hosts.reduce((acc: MonitoringAlarm[], host: MonitoringZabbixHost) => {
            return [
              ...acc,
              ...host.issues]
          }, [])
          this.alarms$ = of(alarms);
          if (!alarms || alarms.length === 0) {
            this.selectedAcknowledgedOption = null;
          }
          this.hostObjects = hosts;
          this.hostObjects.forEach(host => {
            switch (host && host.alarmState && host.alarmState.background) {
              case "LIME":
                host.alarmState.background = '#28A745';
                break;
              case "YELLOW":
                host.alarmState.background = '#ffc107';
                break;
              case "RED":
                host.alarmState.background = '#dc3545';
                break;
            }
          });
          this.cdref.detectChanges();
          this.clearSelectedAlarms(alarms);
        }

      }
      )

    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.configureAlarmTable();
      this.refreshAlarms$.next('refresh')
    })
  }

  ngOnInit(): void {
    this.tableConfiguration = {
      paginate: true,
      sortableMode: 'multiple',
      defaultPagination: 250,
      itemsPerPageOptions: [100, 250, 500],
      exportFilename: 'monitoring-alarms-' + this.url
    }
    this.configureAlarmTable();
    this.refreshAlarms$.next('refresh');
  }

  ngAfterViewInit() {
    this.menuChangeSubscription = this.menuBehaviorSubjectService.changeOccured.subscribe(data => {
      if (data?.action === "MENU_CREATED") {
        this.monitoringAlarmSection = this.menuService.getMonitoringAlarmSectionByUrl(this.$state.params.alarmType);
        this.displayMap = this.monitoringAlarmSection && this.monitoringAlarmSection.displayMap;
        //  this.hostObjects = this.monitoringAlarmSection && this.monitoringAlarmSection.hostObjects;
        if (!this.monitoringAlarmSection || String(this.monitoringAlarmSection.projectId) !== String(this.projectId)) {
          this.$state.go("home", {
            projectId: this.projectId
          });
        } else {
          this.pageTitle = this.monitoringAlarmSection?.name.find(section => section.language === this.translate.currentLang)?.text;
          this.cdref.detectChanges();
        }
      }
    })
  }

  configureAlarmTable = () => {
    this.acknowledgedOptions = [{
      label: this.translate.instant('I18N_GENERAL.ALL'),
      value: null
    }, {
      label: this.translate.instant('I18N_GENERAL.YES'),
      value: true
    },
    {
      label: this.translate.instant('I18N_GENERAL.NO'),
      value: false
    }];

    this.severityOptions = [
      {
        label: this.translate.instant('I18N_MONITORING.LOW'),
        value: 2
      },
      {
        label: this.translate.instant('I18N_MONITORING.MEDIUM'),
        value: 3
      },
      {
        label: this.translate.instant('I18N_MONITORING.HIGH'),
        value: 4
      },
      {
        label: this.translate.instant('I18N_MONITORING.FATAL'),
        value: 5
      }
    ];
  }

  acknowledgeAlarm = async () => {
    const selectedAlarms = [...this.selectedAlarmList];
    if (selectedAlarms && selectedAlarms.length > 0) {
      const projectId = selectedAlarms[0].projectId;
      const eventIds = selectedAlarms.map(alarm => alarm.eventId);

      try {
        await this.faultMonitoringDataService.acknowledgeMonitoringAlarms(projectId, this.url, eventIds);
        this.selectedAlarmList = selectedAlarms.map(alarm => {
          return {
            ...alarm,
            acknowledged: true
          }
        })
      } catch (error) {

      }
    }
    this.refreshAlarms$.next('refresh');
  }

  investigateAlarm = () => {
    const alarms = [...this.selectedAlarmList];
    if (alarms && alarms.length > 0) {
      const zabbixURL = alarms[0].zabbixURL;
      if (zabbixURL) {
        const matchedResult = zabbixURL.match(/^https?:\/\/[^#?\/]+(:?\/zabbix)*/);
        if (matchedResult) {
          const zabbixBaseURL = matchedResult[0];
          for (let i = 0; i < alarms.length; i++) {
            window.open(zabbixBaseURL + '/tr_events.php?triggerid=' + alarms[i].triggerId + '&eventid=' + alarms[i].eventId);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
            detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL')
          });
        }

      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
          detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL_SELECTED_TRAIN')
        });
      }
    }
  }

  clearSelectedAlarms = (alarms: MonitoringAlarm[]) => {
    this.selectedAlarmList = alarms && alarms.length > 0 ? alarms.filter(alarm => this.selectedAlarmList.map(alarm => alarm.eventId).includes(alarm.eventId)) : [];
  }

  onSearchAlarm(searchTerm: string) {
    this.alarmSearchTerm = searchTerm
  }


  onTableAlarmFilter = (filters: any) => {
    this.alarmTableFilters = filters;
  }

  onAssetSearch = (data: {
    asset: string;
    keyword: string
  }) => {
    switch (data.asset) {
      case 'host':
        this.alarmSearchTerm = data.keyword;
        break;
      default:
        break;
    }
  }

  onAutoRefreshChange = () => {
    let customAlarmPageFilters = localStorage.getItem('custom-page-alarm-filters-' + this.projectId);
    if (customAlarmPageFilters) {
      customAlarmPageFilters = {
        ...JSON.parse(customAlarmPageFilters),
        autoRefresh: this.autoRefresh
      }
      localStorage.setItem(
        `custom-page-alarm-filters-${this.projectId}`,
        JSON.stringify(customAlarmPageFilters)
      );
    }
  }

  ngOnDestroy() {
    this.menuChangeSubscription.unsubscribe();
    this.languageChangeSubscription.unsubscribe();
    this.pollingMonitoringAlarmsSubscription.unsubscribe();
  }

}
